import React from 'react';

import { Provider } from 'react-redux';
import {
	BrowserRouter,
	Route,
	Routes,
	useLocation,
	useNavigate,
} from 'react-router-dom';

import posthog from 'posthog-js';
import { createRoot } from 'react-dom/client';

import ConfigList from './components/ConfigList';
import Layout from './components/Layout';
import MerchantConfigPage from './components/MerchantConfigPage';
import Onboarding from './components/onboarding/Onboarding';
import OnboardingPageWrapper from './components/onboarding/OnboardingPageWrapper';
import OnboardingStatementUpload from './components/onboarding/OnboardingStatementUpload';
import ProtectedRoute from './components/ProtectedRoute';
import PricingConfigurationWizard from './components/settings/PricingConfigurationWizard';
import StatementUpload from './components/StatementUpload';
import { ToastProvider, useToast } from './context/ToastContext';
import { useOnboarding } from './hooks/useOnboarding';
import AdminPage from './pages/AdminPage';
import AnalysisViewPage from './pages/AnalysisViewPage';
import ApiDocsPage from './pages/ApiDocsPage';
import EnterpriseDetailPage from './pages/EnterpriseDetailPage';
import GoogleOAuthCallback from './pages/GoogleOAuthCallback';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import SettingsPage from './pages/SettingsPage';
import StatementAnalyzerPage from './pages/StatementAnalyzer';
import SubscriptionPage from './pages/SubscriptionPage';
import TeamPage from './pages/TeamPage';
import TenantDetailsPage from './pages/TenantDetailsPage';
import TenantPage from './pages/TenantPage';
import UserProfilePage from './pages/UserProfilePage';
import VerifyUserPage from './pages/VerifyUserPage';
import { AppAuthProvider } from './providers/AppAuthProvider';
import { TenantProvider } from './providers/TenantProvider';
import { ThemeProvider } from './providers/ThemeProvider';
import { setToastContext, store } from './store';
import './styles/index.css';
import { setGlobalApiUrl } from './utils/globalConfig';
import { addBreadcrumb } from './utils/errorReporting';

// PostHog initialization for session replay
const initializePostHog = () => {
	if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'development') {
		if (process.env.REACT_APP_POSTHOG_APP_ID) {
			posthog.init(process.env.REACT_APP_POSTHOG_APP_ID, {
				api_host:
					process.env.REACT_APP_POSTHOG_HOST || 'https://us.posthog.com',
				persistence: 'localStorage',
				autocapture: true,
				capture_pageview: true,
				capture_pageleave: true,
				session_recording: {
					maskAllInputs: false,
					maskTextSelector: '[data-ph-mask]',
				},
			});
		} else {
			console.warn(
				'PostHog tracking disabled: REACT_APP_POSTHOG_APP_ID not provided',
			);
		}
	}
};

const ComponentWithQueryParams = ({
	Component,
	...props
}: {
	Component: React.ComponentType<any>;
	[key: string]: any;
}) => {
	const location = useLocation();
	const searchParams = new URLSearchParams(location.search);
	const queryProps: Record<string, string> = {};
	searchParams.forEach((value, key) => {
		if (key.toLowerCase() === 'environment') {
			queryProps[key.toLowerCase()] = value.trim().toLowerCase();
		} else {
			queryProps[key] = value;
		}
	});
	const allProps = { ...props, ...queryProps };
	return <Component {...allProps} />;
};

// Wrapper component for onboarding config that uses the useOnboarding hook
const OnboardingConfigWrapper = () => {
	const navigate = useNavigate();
	const { completeStepById } = useOnboarding();

	return (
		<PricingConfigurationWizard
			isOpen={true}
			onClose={() => navigate('/onboarding')}
			onComplete={() => {
				// Complete the onboarding step and navigate to next step
				completeStepById(3);
				navigate('/onboarding/team');
			}}
			isFullPage={true}
			onBack={() => navigate('/onboarding')}
		/>
	);
};

// Component to connect Redux toast middleware with React context
const ToastConnector: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const { showToast } = useToast();

	React.useEffect(() => {
		setToastContext({ showToast });
	}, [showToast]);

	return <>{children}</>;
};

// Custom fallback component for error boundary
const ErrorFallback: React.FC<{
	error: Error;
	resetError: () => void;
}> = ({ error, resetError }) => {
	return (
		<div className="min-h-screen bg-gray-50 flex items-center justify-center">
			<div className="max-w-md mx-auto bg-red-50 border border-red-200 rounded-lg p-6">
				<h2 className="text-lg font-semibold text-red-800 mb-2">
					Something went wrong
				</h2>
				<p className="text-red-600 mb-4">
					The application encountered an error. Please try again or refresh the
					page.
				</p>
				<div className="flex space-x-3">
					<button
						onClick={resetError}
						className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
					>
						Try Again
					</button>
					<button
						onClick={() => window.location.reload()}
						className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
					>
						Refresh Page
					</button>
				</div>
				{process.env.NODE_ENV === 'development' && error && (
					<details className="mt-4">
						<summary className="text-sm text-red-600 cursor-pointer">
							Error Details (Development Only)
						</summary>
						<pre className="text-xs text-red-500 mt-2 whitespace-pre-wrap">
							{error.toString()}
						</pre>
					</details>
				)}
			</div>
		</div>
	);
};

class AppErrorBoundary extends React.Component<
	{ children: React.ReactNode },
	{ error: Error | null }
> {
	state: { error: Error | null } = { error: null };

	static getDerivedStateFromError(error: Error) {
		return { error };
	}

	componentDidCatch(error: Error) {
		addBreadcrumb(
			`Error boundary activated: ${error.message}`,
			'error',
			'error',
		);
	}

	resetError = () => {
		this.setState({ error: null });
	};

	render() {
		if (this.state.error) {
			return (
				<ErrorFallback error={this.state.error} resetError={this.resetError} />
			);
		}

		return this.props.children;
	}
}

// OLD: Tenant initializer component - REPLACED by TenantProvider
/*
const TenantInitializer: React.FC = () => {
    const dispatch = useAppDispatch();

    React.useEffect(() => {
        const initializeTenant = async () => {
            try {
                // Import tenant utilities and Redux actions
                const {
                    getTenantIdentifier,
                    setTenantId,
                    getTenantId,
                } = await import('./utils/tenantUtils');
                const { setTenant, setLoading } = await import('./store/tenantSlice');

                // Check if tenant_id already in localStorage
                const existingTenantId = getTenantId();

                // Get tenant identifier from domain or URL
                const { tenantId, subdomain } = getTenantIdentifier();

                // If we have a tenant identifier from domain/URL, always refresh tenant data
                // This ensures tenant config is loaded even if localStorage has stale data
                if (existingTenantId && !tenantId && !subdomain) {
                    // No new tenant identifier found, and we already have one in storage
                    // Skip initialization to avoid unnecessary API calls
                    return;
                }

                if (!tenantId && !subdomain) {
                    // No tenant identifier found, skip initialization
                    return;
                }

                // Set loading state
                dispatch(setLoading(true));

                // Get API URL
                const currentApiUrl =
                    process.env.REACT_APP_STATEMENT_API ||
                    new URLSearchParams(window.location.search).get('apiUrl') ||
                    'http://localhost:5006';

                const baseUrl = currentApiUrl.endsWith('/api') ? currentApiUrl : `${currentApiUrl}/api`;

                // Call public tenant config API
                let response: Response;
                if (tenantId) {
                    response = await fetch(
                        `${baseUrl}/tenant/config?tenant_id=${encodeURIComponent(tenantId)}`
                    );
                } else if (subdomain) {
                    response = await fetch(
                        `${baseUrl}/tenant/config?subdomain=${encodeURIComponent(subdomain)}`
                    );
                } else {
                    return;
                }

                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.tenant) {
                        // Store tenant_id in localStorage
                        setTenantId(data.tenant.id);
                        // Store full tenant data in Redux (this also sets loading to false)
                        dispatch(setTenant(data.tenant));
                        addBreadcrumb(
                            `Tenant initialized: ${data.tenant.name} (${data.tenant.id})`,
                            'tenant',
                            'info'
                        );
                    } else {
                        // Tenant not found - set loading to false
                        dispatch(setLoading(false));
                    }
                } else {
                    // Tenant not found or error - log but don't fail, set loading to false
                    console.warn('Failed to load tenant config:', response.status);
                    dispatch(setLoading(false));
                }
            } catch (error) {
                // Non-fatal error - log but don't fail app initialization
                console.error('Error initializing tenant:', error);
                addBreadcrumb('Tenant initialization failed', 'tenant', 'warning');
                // Set loading to false on error (setLoading is already imported above)
                dispatch(setLoading(false));
            }
        };

        initializeTenant();
    }, [dispatch]);

    return null; // This component doesn't render anything
};
*/

const AppRoot = () => {
	// Extract API URL from environment or URL params
	const apiUrl =
		process.env.REACT_APP_STATEMENT_API ||
		new URLSearchParams(window.location.search).get('apiUrl') ||
		'http://localhost:8000';

	// Initialize PostHog and set global API URL
	React.useEffect(() => {
		// Initialize PostHog for session replay
		initializePostHog();

		// Set global API URL
		setGlobalApiUrl(apiUrl);

		// Add breadcrumb for app initialization
		addBreadcrumb('Payment Statement app initialized', 'navigation', 'info');
	}, [apiUrl]);

	return (
		<AppErrorBoundary>
			<React.StrictMode>
				<Provider store={store}>
					<TenantProvider>
						<AppAuthProvider>
							<ToastProvider>
								<ToastConnector>
									<ThemeProvider>
										<BrowserRouter
											future={{
												v7_startTransition: true,
												v7_relativeSplatPath: true,
											}}
										>
											<Routes>
												<Route path="/auth" element={<LoginPage />} />
												<Route
													path="/verify-email"
													element={<VerifyUserPage />}
												/>
												<Route
													path="/google-oauth-callback"
													element={<GoogleOAuthCallback />}
												/>
												<Route
													path="/onboarding"
													element={
														<ProtectedRoute>
															<Layout>
																<Onboarding />
															</Layout>
														</ProtectedRoute>
													}
												/>
												<Route
													path="/onboarding/upload"
													element={
														<ProtectedRoute>
															<Layout>
																<OnboardingPageWrapper
																	stepId={1}
																	showArrowButton={false}
																>
																	<OnboardingStatementUpload />
																</OnboardingPageWrapper>
															</Layout>
														</ProtectedRoute>
													}
												/>
												<Route
													path="/onboarding/analysis/:fileId"
													element={
														<ProtectedRoute>
															<Layout>
																<OnboardingPageWrapper stepId={1}>
																	<ComponentWithQueryParams
																		Component={AnalysisViewPage}
																		mfe={false}
																	/>
																</OnboardingPageWrapper>
															</Layout>
														</ProtectedRoute>
													}
												/>
												<Route
													path="/onboarding/subscribe"
													element={
														<ProtectedRoute>
															<Layout>
																<OnboardingPageWrapper stepId={2}>
																	<SubscriptionPage />
																</OnboardingPageWrapper>
															</Layout>
														</ProtectedRoute>
													}
												/>
												<Route
													path="/onboarding/config/new"
													element={
														<ProtectedRoute>
															<Layout>
																<OnboardingPageWrapper stepId={3}>
																	<OnboardingConfigWrapper />
																</OnboardingPageWrapper>
															</Layout>
														</ProtectedRoute>
													}
												/>
												<Route
													path="/onboarding/team"
													element={
														<ProtectedRoute>
															<Layout>
																<OnboardingPageWrapper stepId={4}>
																	<TeamPage />
																</OnboardingPageWrapper>
															</Layout>
														</ProtectedRoute>
													}
												/>
												<Route
													path="/"
													element={
														<ProtectedRoute>
															<HomePage />
														</ProtectedRoute>
													}
												/>
												<Route
													path="/reports"
													element={
														<ProtectedRoute>
															<Layout>
																<ComponentWithQueryParams
																	Component={StatementAnalyzerPage}
																	mfe={false}
																/>
															</Layout>
														</ProtectedRoute>
													}
												/>
												<Route
													path="/config"
													element={
														<ProtectedRoute>
															<Layout>
																<ComponentWithQueryParams
																	Component={ConfigList}
																	mfe={false}
																/>
															</Layout>
														</ProtectedRoute>
													}
												/>
												<Route
													path="/config/new"
													element={
														<ProtectedRoute>
															<Layout>
																<ComponentWithQueryParams
																	Component={MerchantConfigPage}
																	mfe={false}
																/>
															</Layout>
														</ProtectedRoute>
													}
												/>
												<Route
													path="/config/:id"
													element={
														<ProtectedRoute>
															<Layout>
																<ComponentWithQueryParams
																	Component={MerchantConfigPage}
																	mfe={false}
																/>
															</Layout>
														</ProtectedRoute>
													}
												/>
												<Route
													path="/upload"
													element={
														<ProtectedRoute>
															<Layout>
																<ComponentWithQueryParams
																	Component={StatementUpload}
																	mfe={false}
																/>
															</Layout>
														</ProtectedRoute>
													}
												/>
												<Route
													path="/subscription"
													element={
														<ProtectedRoute>
															<Layout>
																<ComponentWithQueryParams
																	Component={SubscriptionPage}
																	mfe={false}
																/>
															</Layout>
														</ProtectedRoute>
													}
												/>
												<Route
													path="/team"
													element={
														<ProtectedRoute>
															<Layout>
																<ComponentWithQueryParams
																	Component={TeamPage}
																	mfe={false}
																/>
															</Layout>
														</ProtectedRoute>
													}
												/>
												<Route
													path="/tenants"
													element={
														<ProtectedRoute>
															<Layout>
																<ComponentWithQueryParams
																	Component={TenantPage}
																	mfe={false}
																/>
															</Layout>
														</ProtectedRoute>
													}
												/>
												<Route
													path="/tenants/:tenantId"
													element={
														<ProtectedRoute>
															<Layout>
																<TenantDetailsPage />
															</Layout>
														</ProtectedRoute>
													}
												/>
												<Route
													path="/settings"
													element={
														<ProtectedRoute>
															<Layout>
																<ComponentWithQueryParams
																	Component={SettingsPage}
																	mfe={false}
																/>
															</Layout>
														</ProtectedRoute>
													}
												/>
												<Route
													path="/profile"
													element={
														<ProtectedRoute>
															<Layout>
																<UserProfilePage />
															</Layout>
														</ProtectedRoute>
													}
												/>
												<Route
													path="/admin"
													element={
														<ProtectedRoute>
															<Layout>
																<AdminPage />
															</Layout>
														</ProtectedRoute>
													}
												/>
												<Route
													path="/admin/enterprises/:enterpriseId"
													element={
														<ProtectedRoute>
															<Layout>
																<EnterpriseDetailPage />
															</Layout>
														</ProtectedRoute>
													}
												/>
												<Route path="/docs" element={<ApiDocsPage />} />
												<Route
													path="/analysis/:fileId"
													element={
														<ProtectedRoute>
															<Layout>
																<ComponentWithQueryParams
																	Component={AnalysisViewPage}
																	mfe={false}
																/>
															</Layout>
														</ProtectedRoute>
													}
												/>
												<Route
													path="*"
													element={
														<ProtectedRoute>
															<Layout>
																<ComponentWithQueryParams
																	Component={StatementAnalyzerPage}
																	mfe={false}
																/>
															</Layout>
														</ProtectedRoute>
													}
												/>
											</Routes>
										</BrowserRouter>
									</ThemeProvider>
								</ToastConnector>
							</ToastProvider>
						</AppAuthProvider>
					</TenantProvider>
				</Provider>
			</React.StrictMode>
		</AppErrorBoundary>
	);
};

const container = document.getElementById('payment-statement-root');
if (container) {
	const root = createRoot(container);
	root.render(<AppRoot />);
} else {
	console.error('App root element #payment-statement-root not found.');
}
